import {
  type CompareMetricBlueprint,
  type MetricBlueprint,
  withCompareMetricPaths,
  withMetricPaths,
} from "@/lib/ai/visualization/data/adapters/metric-binding";

export type DerivedRankDependency = {
  numeratorKey: string;
  rankKey: string;
  denominatorKey: string;
};

function buildRatioAggregationOptions(options?: {
  offset?: number;
  zeroDenominatorValue?: number | null;
}) {
  if (!options) return null;

  return {
    ...(options.offset !== undefined ? { offset: options.offset } : null),
    ...("zeroDenominatorValue" in options
      ? { zeroDenominatorValue: options.zeroDenominatorValue }
      : null),
  };
}

function ratioAggregation(
  numeratorKey: string,
  denominatorKey: string,
  options?: {
    offset?: number;
    zeroDenominatorValue?: number | null;
  },
) {
  return {
    kind: "ratio" as const,
    numeratorKey,
    denominatorKey,
    ...(buildRatioAggregationOptions(options) ?? null),
  };
}

function rankAggregation(numeratorKey: string, denominatorKey: string) {
  return ratioAggregation(numeratorKey, denominatorKey, {
    offset: 1,
    zeroDenominatorValue: null,
  });
}

function internalMetric(metric: MetricBlueprint): MetricBlueprint {
  return {
    ...metric,
    internal: true,
    defaultVisible: false,
    exportable: false,
  };
}

function internalCompareMetric(
  metric: CompareMetricBlueprint,
): CompareMetricBlueprint {
  return {
    ...metric,
    internal: true,
    defaultVisible: false,
    exportable: false,
  };
}

function movingAverageMetric(
  metric: MetricBlueprint,
  options: {
    metricFamily: string;
    movingAverageWindowDays: 7 | 28;
  },
): MetricBlueprint {
  return {
    ...metric,
    lineInterpolation: "monotoneX",
    metricFamily: options.metricFamily,
    movingAverageWindowDays: options.movingAverageWindowDays,
  };
}

export const SA_TIMESERIES_DERIVED_RANK_DEPENDENCIES: readonly DerivedRankDependency[] =
  [
    {
      numeratorKey: "aor7dSumOfPosition",
      rankKey: "aor7d",
      denominatorKey: "soi7d",
    },
    {
      numeratorKey: "aor28dSumOfPosition",
      rankKey: "aor28d",
      denominatorKey: "soi28d",
    },
  ];

export const SA_COMPARE_DERIVED_RANK_DEPENDENCIES: readonly DerivedRankDependency[] =
  [
    {
      numeratorKey: "currentSumOfPosition7d",
      rankKey: "currentAvgRank7d",
      denominatorKey: "currentImpressions7d",
    },
    {
      numeratorKey: "previousSumOfPosition7d",
      rankKey: "previousAvgRank7d",
      denominatorKey: "previousImpressions7d",
    },
    {
      numeratorKey: "currentSumOfPosition28d",
      rankKey: "currentAvgRank28d",
      denominatorKey: "currentImpressions28d",
    },
    {
      numeratorKey: "previousSumOfPosition28d",
      rankKey: "previousAvgRank28d",
      denominatorKey: "previousImpressions28d",
    },
  ];

/**
 * SA aggregated timeseries metrics.
 */
export const SA_TIMESERIES_METRICS: MetricBlueprint[] = withMetricPaths([
  {
    key: "sumOfClicks",
    unit: "count",
    aliases: ["clicks"],
  },
  {
    key: "sumOfImpressions",
    unit: "count",
    aliases: ["impressions"],
  },
  internalMetric({
    key: "sumOfPosition",
    sourceKey: "sumOfPosition",
  }),
  {
    key: "ctr",
    unit: "percent",
    aggregation: ratioAggregation("sumOfClicks", "sumOfImpressions"),
  },
  {
    key: "avgOfRank",
    unit: "rank",
    aliases: ["averageOfRank"],
    aggregation: rankAggregation("sumOfPosition", "sumOfImpressions"),
  },
  { key: "distinctQueries", unit: "count" },
  { key: "distinctURLs", unit: "count" },
  movingAverageMetric(
    {
      key: "soc7d",
      unit: "count",
      aliases: ["sumOfClicks7d", "clicks7d"],
    },
    { metricFamily: "sumOfClicks", movingAverageWindowDays: 7 },
  ),
  movingAverageMetric(
    {
      key: "soc28d",
      unit: "count",
      aliases: ["sumOfClicks28d", "clicks28d"],
    },
    { metricFamily: "sumOfClicks", movingAverageWindowDays: 28 },
  ),
  movingAverageMetric(
    {
      key: "soi7d",
      unit: "count",
      aliases: ["sumOfImpressions7d", "impressions7d"],
    },
    { metricFamily: "sumOfImpressions", movingAverageWindowDays: 7 },
  ),
  movingAverageMetric(
    {
      key: "soi28d",
      unit: "count",
      aliases: ["sumOfImpressions28d", "impressions28d"],
    },
    { metricFamily: "sumOfImpressions", movingAverageWindowDays: 28 },
  ),
  internalMetric({
    key: "aor7dSumOfPosition",
    sourceKey: "sumOfPosition7d",
  }),
  movingAverageMetric(
    {
      key: "ctr7d",
      unit: "percent",
      aliases: ["clickThroughRate7d"],
      aggregation: ratioAggregation("soc7d", "soi7d"),
    },
    { metricFamily: "ctr", movingAverageWindowDays: 7 },
  ),
  movingAverageMetric(
    {
      key: "ctr28d",
      unit: "percent",
      aliases: ["clickThroughRate28d"],
      aggregation: ratioAggregation("soc28d", "soi28d"),
    },
    { metricFamily: "ctr", movingAverageWindowDays: 28 },
  ),
  movingAverageMetric(
    {
      key: "aor7d",
      unit: "rank",
      aliases: ["avgOfRank7d", "averageOfRank7d"],
      aggregation: rankAggregation("aor7dSumOfPosition", "soi7d"),
    },
    { metricFamily: "avgOfRank", movingAverageWindowDays: 7 },
  ),
  internalMetric({
    key: "aor28dSumOfPosition",
    sourceKey: "sumOfPosition28d",
  }),
  movingAverageMetric(
    {
      key: "aor28d",
      unit: "rank",
      aliases: ["avgOfRank28d", "averageOfRank28d"],
      aggregation: rankAggregation("aor28dSumOfPosition", "soi28d"),
    },
    { metricFamily: "avgOfRank", movingAverageWindowDays: 28 },
  ),
  movingAverageMetric(
    {
      key: "dq7d",
      unit: "count",
      aliases: ["distinctQueries7d"],
    },
    { metricFamily: "distinctQueries", movingAverageWindowDays: 7 },
  ),
  movingAverageMetric(
    {
      key: "dq28d",
      unit: "count",
      aliases: ["distinctQueries28d"],
    },
    { metricFamily: "distinctQueries", movingAverageWindowDays: 28 },
  ),
  movingAverageMetric(
    {
      key: "du7d",
      unit: "count",
      aliases: ["distinctURLs7d", "distinctUrls7d"],
    },
    { metricFamily: "distinctURLs", movingAverageWindowDays: 7 },
  ),
  movingAverageMetric(
    {
      key: "du28d",
      unit: "count",
      aliases: ["distinctURLs28d", "distinctUrls28d"],
    },
    { metricFamily: "distinctURLs", movingAverageWindowDays: 28 },
  ),
]);

/**
 * SA aggregated group summary metrics.
 */
export const SA_GROUP_METRICS: MetricBlueprint[] = withMetricPaths([
  {
    key: "totalClicks",
    unit: "count",
    aliases: ["clicks", "sumOfClicks"],
  },
  {
    key: "totalImpressions",
    unit: "count",
    aliases: ["impressions", "sumOfImpressions"],
  },
  internalMetric({
    key: "sumOfPosition",
    sourceKey: "sumOfPosition",
  }),
  {
    key: "ctr",
    unit: "percent",
    aggregation: ratioAggregation("totalClicks", "totalImpressions"),
  },
  {
    key: "avgRank",
    unit: "rank",
    aliases: ["avgOfRank", "averageOfRank"],
    aggregation: rankAggregation("sumOfPosition", "totalImpressions"),
  },
  {
    key: "latestUniqueQueries",
    unit: "count",
    aliases: ["distinctQueries"],
  },
  {
    key: "latestUniqueUrls",
    unit: "count",
    aliases: ["distinctURLs", "distinctUrls"],
  },
]);

/**
 * 比較用 moving average を、対応する基礎metricと同じsemantic familyへ正規化する。
 * 省略keyを独立familyとして扱うと、1つの要求metricだけでscatterが成立してしまう。
 */
function movingAverageCompareMetric(
  metric: CompareMetricBlueprint,
): CompareMetricBlueprint {
  const sourceMetric = SA_TIMESERIES_METRICS.find(
    (candidate) => candidate.key === metric.sourceKey,
  );
  if (!sourceMetric?.metricFamily || !sourceMetric.movingAverageWindowDays) {
    throw new Error(
      `Search Analytics comparison moving average '${metric.sourceKey}' requires canonical family metadata`,
    );
  }
  return {
    ...metric,
    lineInterpolation: "monotoneX",
    metricFamily: sourceMetric.metricFamily,
    movingAverageWindowDays: sourceMetric.movingAverageWindowDays,
  };
}

/**
 * SA aggregated compare timeseries metrics.
 */
export const SA_COMPARE_TIMESERIES_METRICS: CompareMetricBlueprint[] =
  withCompareMetricPaths([
    {
      sourceKey: "sumOfClicks",
      currentKey: "currentClicks",
      previousKey: "previousClicks",
      unit: "count",
      baseAliases: ["sumOfClicks", "clicks"],
    },
    {
      sourceKey: "sumOfImpressions",
      currentKey: "currentImpressions",
      previousKey: "previousImpressions",
      unit: "count",
      baseAliases: ["sumOfImpressions", "impressions"],
    },
    internalCompareMetric({
      sourceKey: "sumOfPosition",
      currentKey: "currentSumOfPosition",
      previousKey: "previousSumOfPosition",
    }),
    {
      sourceKey: "ctr",
      currentKey: "currentCtr",
      previousKey: "previousCtr",
      unit: "percent",
      currentAggregation: ratioAggregation(
        "currentClicks",
        "currentImpressions",
      ),
      previousAggregation: ratioAggregation(
        "previousClicks",
        "previousImpressions",
      ),
      baseAliases: ["ctr"],
    },
    {
      sourceKey: "avgOfRank",
      currentKey: "currentAvgRank",
      previousKey: "previousAvgRank",
      unit: "rank",
      currentAggregation: rankAggregation(
        "currentSumOfPosition",
        "currentImpressions",
      ),
      previousAggregation: rankAggregation(
        "previousSumOfPosition",
        "previousImpressions",
      ),
      baseAliases: ["avgOfRank", "averageOfRank"],
    },
    {
      sourceKey: "distinctQueries",
      currentKey: "currentDistinctQueries",
      previousKey: "previousDistinctQueries",
      unit: "count",
      baseAliases: ["distinctQueries"],
    },
    {
      sourceKey: "distinctURLs",
      currentKey: "currentDistinctUrls",
      previousKey: "previousDistinctUrls",
      unit: "count",
      baseAliases: ["distinctURLs", "distinctUrls"],
    },
    movingAverageCompareMetric({
      sourceKey: "soc7d",
      currentKey: "currentClicks7d",
      previousKey: "previousClicks7d",
      unit: "count",
      baseAliases: ["soc7d", "sumOfClicks7d", "clicks7d"],
    }),
    movingAverageCompareMetric({
      sourceKey: "soc28d",
      currentKey: "currentClicks28d",
      previousKey: "previousClicks28d",
      unit: "count",
      baseAliases: ["soc28d", "sumOfClicks28d", "clicks28d"],
    }),
    movingAverageCompareMetric({
      sourceKey: "soi7d",
      currentKey: "currentImpressions7d",
      previousKey: "previousImpressions7d",
      unit: "count",
      baseAliases: ["soi7d", "sumOfImpressions7d", "impressions7d"],
    }),
    movingAverageCompareMetric({
      sourceKey: "soi28d",
      currentKey: "currentImpressions28d",
      previousKey: "previousImpressions28d",
      unit: "count",
      baseAliases: ["soi28d", "sumOfImpressions28d", "impressions28d"],
    }),
    internalCompareMetric({
      sourceKey: "sumOfPosition7d",
      currentKey: "currentSumOfPosition7d",
      previousKey: "previousSumOfPosition7d",
    }),
    movingAverageCompareMetric({
      sourceKey: "ctr7d",
      currentKey: "currentCtr7d",
      previousKey: "previousCtr7d",
      unit: "percent",
      currentAggregation: ratioAggregation(
        "currentClicks7d",
        "currentImpressions7d",
      ),
      previousAggregation: ratioAggregation(
        "previousClicks7d",
        "previousImpressions7d",
      ),
      baseAliases: ["ctr7d", "clickThroughRate7d"],
    }),
    movingAverageCompareMetric({
      sourceKey: "ctr28d",
      currentKey: "currentCtr28d",
      previousKey: "previousCtr28d",
      unit: "percent",
      currentAggregation: ratioAggregation(
        "currentClicks28d",
        "currentImpressions28d",
      ),
      previousAggregation: ratioAggregation(
        "previousClicks28d",
        "previousImpressions28d",
      ),
      baseAliases: ["ctr28d", "clickThroughRate28d"],
    }),
    movingAverageCompareMetric({
      sourceKey: "aor7d",
      currentKey: "currentAvgRank7d",
      previousKey: "previousAvgRank7d",
      unit: "rank",
      currentAggregation: rankAggregation(
        "currentSumOfPosition7d",
        "currentImpressions7d",
      ),
      previousAggregation: rankAggregation(
        "previousSumOfPosition7d",
        "previousImpressions7d",
      ),
      baseAliases: ["aor7d", "avgOfRank7d", "averageOfRank7d"],
    }),
    internalCompareMetric({
      sourceKey: "sumOfPosition28d",
      currentKey: "currentSumOfPosition28d",
      previousKey: "previousSumOfPosition28d",
    }),
    movingAverageCompareMetric({
      sourceKey: "aor28d",
      currentKey: "currentAvgRank28d",
      previousKey: "previousAvgRank28d",
      unit: "rank",
      currentAggregation: rankAggregation(
        "currentSumOfPosition28d",
        "currentImpressions28d",
      ),
      previousAggregation: rankAggregation(
        "previousSumOfPosition28d",
        "previousImpressions28d",
      ),
      baseAliases: ["aor28d", "avgOfRank28d", "averageOfRank28d"],
    }),
    movingAverageCompareMetric({
      sourceKey: "dq7d",
      currentKey: "currentDistinctQueries7d",
      previousKey: "previousDistinctQueries7d",
      unit: "count",
      baseAliases: ["dq7d", "distinctQueries7d"],
    }),
    movingAverageCompareMetric({
      sourceKey: "dq28d",
      currentKey: "currentDistinctQueries28d",
      previousKey: "previousDistinctQueries28d",
      unit: "count",
      baseAliases: ["dq28d", "distinctQueries28d"],
    }),
    movingAverageCompareMetric({
      sourceKey: "du7d",
      currentKey: "currentDistinctUrls7d",
      previousKey: "previousDistinctUrls7d",
      unit: "count",
      baseAliases: ["du7d", "distinctURLs7d", "distinctUrls7d"],
    }),
    movingAverageCompareMetric({
      sourceKey: "du28d",
      currentKey: "currentDistinctUrls28d",
      previousKey: "previousDistinctUrls28d",
      unit: "count",
      baseAliases: ["du28d", "distinctURLs28d", "distinctUrls28d"],
    }),
  ]);
